import type { Vec2 } from '../../../geo/types';
import type { Frame, MeshBuilder } from '../../mesh/meshBuilder';
import { orientedFace, planPoint, triangulateRing } from '../solids';
import { corners } from './genericBlock';
import { transverseRoof } from './gothic/roofs';
import { edgePanel, openedWall, type MasonryStyles } from './masonry';
import type { ConventRange } from './conventRange';

export interface RangeRoofGables {
  readonly centres: readonly number[];
  readonly halfWidth: number;
  readonly v0: number;
  readonly v1: number;
  readonly eaves: number;
  readonly ridge: number;
  readonly sill: number;
  readonly top: number;
}

/** Cut the recorded dormers into their host slope; no intact roof crosses their openings. */
export function buildRangeRoofGables(
  builder: MeshBuilder,
  frame: Frame,
  styles: MasonryStyles,
  range: Pick<ConventRange, 'length' | 'width' | 'eaves' | 'ridge'>,
  g: RangeRoofGables
): void {
  const rect = {
      u0: -range.length / 2,
      u1: range.length / 2,
      v0: -range.width / 2,
      v1: range.width / 2,
    },
    holes = g.centres.map((u) =>
      corners({ u0: u - g.halfWidth, u1: u + g.halfWidth, v0: g.v0, v1: g.v1 })
    ),
    roofAt = (v: number) => range.eaves + (range.ridge - range.eaves) * (1 - v / rect.v1);
  for (const sign of [-1, 1]) {
    const edge = sign < 0 ? rect.v0 : rect.v1,
      slope = corners({ ...rect, v0: Math.min(edge, 0), v1: Math.max(edge, 0) });
    for (const triangle of triangulateRing(slope, sign > 0 ? holes : []))
      orientedFace(
        builder,
        styles.roof,
        frame,
        triangle.map(([u, v]) => planPoint(u, roofAt(Math.abs(v)), v)),
        [0, 1, -sign]
      );
  }
  for (const u of g.centres) {
    const u0 = u - g.halfWidth,
      u1 = u + g.halfWidth;
    for (const front of [false, true]) {
      const v = front ? g.v1 : g.v0,
        edge: readonly [Vec2, Vec2] = front
          ? [
              [u1, v],
              [u0, v],
            ]
          : [
              [u0, v],
              [u1, v],
            ],
        width = 2 * g.halfWidth;
      openedWall(
        builder,
        styles,
        frame,
        edgePanel(...edge),
        [
          [0, roofAt(v)],
          [width, roofAt(v)],
          [width, g.eaves],
          [width / 2, g.ridge],
          [0, g.eaves],
        ],
        front
          ? [
              [
                [width / 2 - 0.3, g.sill],
                [width / 2 + 0.3, g.sill],
                [width / 2 + 0.3, g.top],
                [width / 2 - 0.3, g.top],
              ],
            ]
          : []
      );
    }
    for (const sign of [-1, 1]) {
      const x = sign < 0 ? u0 : u1;
      orientedFace(
        builder,
        styles.wall,
        frame,
        [
          planPoint(x, roofAt(g.v0), g.v0),
          planPoint(x, roofAt(g.v1), g.v1),
          planPoint(x, g.eaves, g.v1),
          planPoint(x, g.eaves, g.v0),
        ],
        [sign, 0, 0]
      );
    }
    transverseRoof(
      builder,
      styles,
      frame,
      { u0, u1, v0: g.v0, v1: g.v1 },
      { eaveY: g.eaves, ridgeY: g.ridge }
    );
  }
}
